/**
 * Organization Plan Types
 *
 * Org plan enum, feature flags, plan hierarchy, pricing, limits, and AI cost
 * caps. Two-layer authorization: org plan gates features, member role gates
 * access within those features.
 *
 * Owned by `@event-manager/api-client` (previously re-exported from the
 * now-removed `@bavard/types-common`).
 */

import { z } from "zod";

// ============================================================================
// Org Plan Enum
// ============================================================================

/** Zod schema for org plan validation */
export const OrgPlanSchema = z.enum(["chat", "standard", "premium"]);

/** Org plan union type */
export type OrgPlan = z.infer<typeof OrgPlanSchema>;

/** Org plan values as const object */
export const ORG_PLAN = {
	CHAT: "chat",
	STANDARD: "standard",
	PREMIUM: "premium",
} as const satisfies Record<string, OrgPlan>;

/** Type guard for OrgPlan */
export function isOrgPlan(value: unknown): value is OrgPlan {
	return OrgPlanSchema.safeParse(value).success;
}

// ============================================================================
// Plan Features
// ============================================================================

/** Features gated by org plan */
export interface OrgFeatures {
	calendar: boolean;
	chat: boolean;
	documents: boolean;
}

/** Plan hierarchy for minimum-plan checks (like role hierarchy) */
export const PLAN_HIERARCHY: Record<OrgPlan, number> = {
	chat: 1,
	standard: 2,
	premium: 3,
};

/**
 * The default (lowest-tier) org plan, derived from the hierarchy. Every new org
 * gets this plan. If plans are renamed or reordered, this picks the lowest one.
 */
export const DEFAULT_ORG_PLAN: OrgPlan = (
	Object.entries(PLAN_HIERARCHY) as [OrgPlan, number][]
).reduce(
	(lowest, [plan, level]) => (level < PLAN_HIERARCHY[lowest] ? plan : lowest),
	Object.keys(PLAN_HIERARCHY)[0] as OrgPlan
);

/** Check if an org plan meets or exceeds the required plan level */
export function hasMinimumPlan(
	orgPlan: OrgPlan,
	requiredPlan: OrgPlan
): boolean {
	return PLAN_HIERARCHY[orgPlan] >= PLAN_HIERARCHY[requiredPlan];
}

/** Feature flags for each plan tier */
export const PLAN_FEATURES: Record<OrgPlan, OrgFeatures> = {
	chat: { chat: true, calendar: false, documents: false },
	standard: { chat: true, calendar: true, documents: false },
	premium: { chat: true, calendar: true, documents: true },
};

/** Get the feature flags for a given plan */
export function getOrgFeatures(plan: OrgPlan): OrgFeatures {
	return PLAN_FEATURES[plan];
}

// ============================================================================
// Plan Display Names & Pricing
// ============================================================================

/** Human-readable display names for each plan (marketing names) */
export const PLAN_DISPLAY_NAMES: Record<OrgPlan, string> = {
	chat: "Chat",
	standard: "Standard",
	premium: "Premium",
};

/** A market has its own list prices; they are never converted from another. */
export const PricingMarketSchema = z.enum(["saudi", "egypt", "global"]);

export type PricingMarket = z.infer<typeof PricingMarketSchema>;

/** Currency each market is priced and charged in, and the VAT added on top. */
export const PRICING_MARKETS: Record<
	PricingMarket,
	{ currency: string; vatPercent: number }
> = {
	saudi: { currency: "SAR", vatPercent: 15 },
	egypt: { currency: "EGP", vatPercent: 14 },
	global: { currency: "USD", vatPercent: 0 },
};

/** Saudi Arabia and Egypt are their own markets; every other country is global. */
export function resolvePricingMarket(
	countryCode: string | null | undefined
): PricingMarket {
	switch (countryCode?.toUpperCase()) {
		case "SA":
			return "saudi";
		case "EG":
			return "egypt";
		default:
			return "global";
	}
}

export const ANNUAL_BILLING_DISCOUNT = 0.2;

/** A plan's price in one market, excl. VAT, in that currency's minor units. */
export const PlanPriceSchema = z.object({
	monthly: z.number().int(),
	yearly: z.number().int(),
	yearlyPerMonth: z.number().int(),
});

export type PlanPrice = z.infer<typeof PlanPriceSchema>;

function planPrice(monthly: number): PlanPrice {
	const yearlyPerMonth = Math.round(monthly * (1 - ANNUAL_BILLING_DISCOUNT));
	return { monthly, yearly: yearlyPerMonth * 12, yearlyPerMonth };
}

/** Approved list price of each plan in each market. */
export const PLAN_PRICING: Record<OrgPlan, Record<PricingMarket, PlanPrice>> = {
	chat: {
		saudi: planPrice(29_900),
		egypt: planPrice(249_000),
		global: planPrice(8900),
	},
	standard: {
		saudi: planPrice(79_900),
		egypt: planPrice(749_000),
		global: planPrice(22_900),
	},
	premium: {
		saudi: planPrice(219_900),
		egypt: planPrice(1_999_000),
		global: planPrice(44_900),
	},
};

/** Plan feature descriptions for pricing pages */
export const PLAN_FEATURE_DESCRIPTIONS: Record<OrgPlan, string[]> = {
	chat: [
		"WhatsApp, Telegram, or Instagram messaging",
		"AI knowledge base (RAG)",
		"AI scheduling assistant",
		"1,000 AI messages/mo",
		"Unlimited staff users & AI agent presets",
		"Knowledge base auto-refresh",
		"Email support",
	],
	standard: [
		"Everything in Chat",
		"Calendar & event management",
		"Analytics dashboard",
		"5,000 AI messages/mo",
		"Waitlist & automatic slot offers",
		"Priority support",
	],
	premium: [
		"Everything in Standard",
		"Document editor & canvas",
		"Advanced analytics & reports",
		"15,000 AI messages/mo",
		"API access",
		"Dedicated support",
	],
};

// ============================================================================
// Plan Limits & Custom Overrides
// ============================================================================

/**
 * Numeric quota limits per plan: ceilings enforced by the application (AI
 * message count, staff seat count, storage bytes). `null` means unlimited.
 */
export interface OrgLimits {
	aiPresets: number | null;
	monthlyMessages: number;
	staffSeats: number | null;
	storageBytes: number;
}

const MEBIBYTE = 1024 * 1024;

/**
 * The single editable source of truth for every per-plan numeric ceiling.
 *
 * Adding a plan means adding exactly ONE row here (plus its entry in
 * `OrgPlanSchema`); changing a ceiling means editing one number — nothing
 * else reads a hard-coded quota. The server resolves the effective value
 * through {@link resolveOrgLimits}, which lets a superadmin per-org override
 * win over the row below.
 *
 * `storageBytes` is the ORG-WIDE total of stored RAG-DOCUMENT bytes — the
 * knowledge base the AI is trained on. It counts every RagDocument row
 * (production, playground, staged, failed) and NOTHING else: canvas/tldraw
 * documents live in a different aggregate and are not billed against this.
 * The per-file ceiling is a separate constant (`MAX_RAG_FILE_BYTES`, 10 MB).
 *
 * The ladder is benchmarked tier-for-tier against Chatbase's "training data"
 * allowance (checked 2026-07-25), our closest comparable, matched by message
 * volume rather than price — our plans deliver the same monthly message count
 * for roughly half their cost:
 *
 *   chat     1,000 msgs  ->  Chatbase Hobby     (500 credits)    10 MB
 *   standard 5,000 msgs  ->  Chatbase Standard  (4,000 credits)  20 MB
 *   premium 15,000 msgs  ->  Chatbase Pro       (15,000 credits) 40 MB
 *
 * In documents rather than bytes: a typical PDF/DOCX knowledge source is well
 * under 1 MB, so these ceilings are roughly 10-20 / 20-40 / 40-80 documents.
 * A pathological 10 MB scan is the exception, not the unit to size against.
 */
export const PLAN_LIMITS: Record<OrgPlan, OrgLimits> = {
	chat: {
		aiPresets: null,
		monthlyMessages: 1000,
		staffSeats: null,
		storageBytes: 10 * MEBIBYTE,
	},
	standard: {
		aiPresets: null,
		monthlyMessages: 5000,
		staffSeats: null,
		storageBytes: 20 * MEBIBYTE,
	},
	premium: {
		aiPresets: null,
		monthlyMessages: 15_000,
		staffSeats: null,
		storageBytes: 40 * MEBIBYTE,
	},
};

/**
 * Enabled-custom-action quota per plan. Mirrors the server's
 * `PLAN_CUSTOM_TOOL_LIMITS` (custom-tool.service.ts) by value. Drafts are
 * unlimited; only enabled custom actions count. A superadmin
 * `customToolLimitOverride` wins over the plan default.
 */
export const PLAN_CUSTOM_TOOL_LIMITS: Record<OrgPlan, number> = {
	chat: 0,
	standard: 5,
	premium: 15,
};

/** Resolve the effective enabled-custom-action quota (override wins). */
export function resolveOrgCustomToolLimit(org: {
	plan: OrgPlan;
	customToolLimitOverride: number | null;
}): number {
	return org.customToolLimitOverride ?? PLAN_CUSTOM_TOOL_LIMITS[org.plan];
}

export const DEMO_ORG_ID = "demo-org-00000000000";

/** Resolve the effective staff-seat limit, with unlimited seats for demo data. */
export function resolveOrgStaffSeatLimit(org: {
	id: string;
	plan: OrgPlan;
	customStaffSeatLimit: number | null;
}): number | null {
	if (org.id === DEMO_ORG_ID) {
		return null;
	}
	return org.customStaffSeatLimit ?? PLAN_LIMITS[org.plan].staffSeats;
}

/** Resolve the effective preset quota; null means unlimited demo data. */
export function resolveOrgAiPresetLimit(org: {
	id: string;
	plan: OrgPlan;
	customAiPresetLimit: number | null;
}): number | null {
	if (org.id === DEMO_ORG_ID) {
		return null;
	}
	return org.customAiPresetLimit ?? PLAN_LIMITS[org.plan].aiPresets;
}

/** Resolve effective quota limits for an organization (custom overrides win). */
export function resolveOrgLimits(org: {
	plan: OrgPlan;
	customAiPresetLimit?: number | null;
	customMonthlyMessageLimit: number | null;
	customStaffSeatLimit: number | null;
	customStorageLimitBytes: bigint | null;
}): OrgLimits {
	const defaults = PLAN_LIMITS[org.plan];
	return {
		aiPresets: org.customAiPresetLimit ?? defaults.aiPresets,
		monthlyMessages: org.customMonthlyMessageLimit ?? defaults.monthlyMessages,
		staffSeats: org.customStaffSeatLimit ?? defaults.staffSeats,
		storageBytes:
			org.customStorageLimitBytes === null
				? defaults.storageBytes
				: Number(org.customStorageLimitBytes),
	};
}

// ============================================================================
// AI Cost Caps (per-plan monthly USD budget for OpenRouter inference)
// ============================================================================

/** Monthly AI inference cost cap per org plan, in USD. */
export const PLAN_AI_COST_LIMITS_USD: Record<OrgPlan, number> = {
	chat: 5,
	standard: 50,
	premium: 500,
};

/**
 * Resolve the monthly AI cost cap for an arbitrary plan string. Falls back to
 * the default plan's cap when the input is unrecognized.
 */
export function resolvePlanAiCostLimitUsd(
	plan: string | null | undefined
): number {
	if (plan && plan in PLAN_AI_COST_LIMITS_USD) {
		return PLAN_AI_COST_LIMITS_USD[plan as OrgPlan];
	}
	return PLAN_AI_COST_LIMITS_USD[DEFAULT_ORG_PLAN];
}
